import { motion } from 'motion/react';

/**
 * DashboardCard — the ONE kachel component for all dashboard items.
 * See UNIFIED_UI_ARCHITECTURE.md §5.5
 *
 * Compact density: 22px metric, 11px uppercase label, 104px min-height.
 * Reference: Odoo mobile dashboard — big relative to kachel, small absolute.
 */
export default function DashboardCard({
  icon,
  title,
  metric,
  subtitle,
  trend,
  alert,
  onClick,
  loading = false,
}) {
  if (loading) {
    return (
      <div
        className="rounded-[var(--aif-radius-lg)] border border-[var(--aif-border-hover)] bg-[var(--aif-bg-surface)] p-[var(--aif-space-4)] min-h-[104px] animate-pulse flex flex-col"
        data-testid="dashboard-card-skeleton"
      >
        <div className="w-5 h-5 rounded bg-[var(--aif-bg-elevated)] mb-2" />
        <div className="h-2.5 w-12 bg-[var(--aif-bg-elevated)] rounded mb-2" />
        <div className="h-5 w-20 bg-[var(--aif-bg-elevated)] rounded mb-1" />
        <div className="flex-1" />
        <div className="h-2.5 w-24 bg-[var(--aif-bg-elevated)] rounded" />
      </div>
    );
  }

  const trendColor = trend
    ? trend.value > 0 ? 'text-green-400' : trend.value < 0 ? 'text-red-400' : 'text-[var(--aif-text-muted)]'
    : null;

  const alertColors = {
    warning: 'text-amber-400',
    error: 'text-red-400',
    info: 'text-blue-400',
  };

  // Alert left-border: 3px colored bar for cards needing attention
  const borderClass = alert
    ? alert.severity === 'error'
      ? 'border-l-[3px] border-l-[var(--aif-color-danger)] border border-[var(--aif-border-hover)]'
      : alert.severity === 'warning'
        ? 'border-l-[3px] border-l-[var(--aif-color-warning)] border border-[var(--aif-border-hover)]'
        : 'border border-[var(--aif-border-hover)]'
    : 'border border-[var(--aif-border-hover)]';

  return (
    <motion.button
      onClick={onClick}
      whileTap={onClick ? { scale: 0.97 } : undefined}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={`w-full text-left rounded-[var(--aif-radius-lg)] ${borderClass} bg-[var(--aif-bg-surface)] p-[var(--aif-space-4)] min-h-[104px] overflow-hidden flex flex-col transition-all hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:border-[var(--aif-border-default)]`}
      data-testid="dashboard-card"
    >
      {/* Icon — 20×20 */}
      {icon && (
        <div className="w-5 h-5 flex items-center justify-center mb-2 flex-shrink-0 text-base leading-none">{icon}</div>
      )}

      {/* Label — 11px uppercase, secondary */}
      <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-[var(--aif-text-secondary)] mb-2 truncate flex-shrink-0">
        {title}
      </div>

      {/* Metric — 22px HERO + optional inline trend */}
      <div className="flex items-baseline gap-1.5 flex-shrink-0" data-testid="card-metric">
        <span className="text-[22px] font-medium text-[var(--aif-text-primary)] leading-[1.1] truncate">
          {metric}
        </span>
        {trend && (
          <span className={`text-[13px] font-medium ${trendColor} flex-shrink-0`} data-testid="card-trend">
            {trend.value > 0 ? '▲' : trend.value < 0 ? '▼' : '→'}{Math.abs(trend.value)}%
          </span>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1 min-h-[4px]" />

      {/* Bottom: subtitle + alert */}
      <div className="flex-shrink-0">
        {subtitle && (
          <div className="text-[12px] text-[var(--aif-text-muted)] mt-1.5 truncate whitespace-nowrap overflow-hidden" data-testid="card-subtitle">
            {subtitle}
          </div>
        )}
        {alert && (
          <div className={`text-[12px] mt-1 font-medium truncate whitespace-nowrap overflow-hidden ${alertColors[alert.severity] || alertColors.info}`} data-testid="card-alert">
            {alert.text}
          </div>
        )}
      </div>
    </motion.button>
  );
}
